import { useQueryClient } from "@tanstack/react-query";
import { getChatApiClientConfig } from "@shared/apis/sidekick/api-factories";
import { appEventEmitter } from "@shared/events/app-event-emitter";
import { buildGetMessagesKey } from "./useGetMessages";
import { toast } from "react-toastify";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { StreamDataMessage } from "./types/StreamDataMessage";
import { appLogger } from "@shared/services/app-logger";

type Props = {
  promptRefId: string | null;
  threadId: string | null | undefined;
  onSuccess(params: { threadId: string; streamData: StreamDataMessage }): void;
  onError(params: { threadId: string }): void;
};

type MessageStreamState =
  | "idle"
  | "awaiting"
  | "executing-tool-call"
  | "loading-message-text-delta"
  | "error"
  | "success"
  | "reasoning"
  | "reconnecting";

function parseStreamData(data: string): StreamDataMessage | { error: unknown } {
  try {
    const streamData: StreamDataMessage = JSON.parse(data);
    return streamData;
  } catch (error) {
    appLogger.error("Error while parsing streamData: ", { error });
    return { error };
  }
}

function applyStateTransition(
  eventSource: StreamDataMessage,
  setState: (state: MessageStreamState) => void,
  startDate: Date
): void {
  if (eventSource.type === "runStepCreated") {
    if (eventSource.data.type === "tool_calls") {
      setState("awaiting");
    }
    if (eventSource.data.type === "message_creation") {
      setState("loading-message-text-delta");
    }
    if (eventSource.data.type === "reasoning") {
      setState("reasoning");
    }
  }
  if (eventSource.type === "runStepCompleted") {
    if (eventSource.data.type === "tool_calls") {
      setState("awaiting");
    }
    // if (eventSource.data.type === "message_creation") {
    //   setState("idle");
    // }
  }
  // if (eventSource.type === "messageCreated") {
  //   setState("loading-message-text-delta");
  // }
  // if (eventSource.type === "messageTextDelta") {
  //   setState("loading-message-text-delta");
  // }
  if (eventSource.type === "toolCallStarted") {
    setState("executing-tool-call");
  }
  if (eventSource.type === "toolCallCompleted") {
    setState("awaiting");
  }
  if (eventSource.type === "messageCompleted") {
    const isResponseMessage = eventSource.data.some((m) =>
      m.uniqueKey.includes("msg_")
    );
    if (isResponseMessage) {
      setState("idle");
      eventSource.metadata = {
        elapsedTime: new Date().getTime() - startDate.getTime(),
      };
    }
  }

  if (eventSource.type === "connectionError") {
    setState("reconnecting");
  }
}

export const useMessageStreamListener = (props: Props) => {
  const { onSuccess, onError, threadId, promptRefId } = props;
  const [state, setState] = useState<MessageStreamState>("idle");
  const connectionRetriesRef = useRef(0);
  const eventSourceRef = useRef<EventSource | null>(null);
  const queryClient = useQueryClient();

  const handleError = useCallback(
    (params: { error: unknown; threadId: string }) => {
      const { error, threadId } = params;
      onError({ threadId });
      setState("error");
      appLogger.error("Failed to process prompt response", { error });
      appEventEmitter.emit({ eventType: "sidekick-prompt-response-failure" });
      // Make the toast less alarming
      toast.info(
        "Connection issue. Please try again or contact support if the issue persists.",
        {
          toastId: "message-response-error",
          autoClose: 8000,
        }
      );
    },
    [onError]
  );

  const handleSuccess = useCallback(
    (params: { threadId: string; startDate: Date }) => {
      const { threadId, startDate } = params;
      setState("success");
      eventSourceRef.current = null;
      onSuccess({ threadId, streamData: { type: "end" } });
      appEventEmitter.emit({
        eventType: "sidekick-prompt-response-success",
        responseTimeMs: new Date().getTime() - startDate.getTime(),
      });
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: buildGetMessagesKey({ threadId }),
        });
      }, 500);
    },
    [onSuccess, queryClient]
  );

  const isReceivingResponse =
    (!!threadId && !!promptRefId && state === "idle") ||
    state === "awaiting" ||
    state === "loading-message-text-delta" ||
    state === "executing-tool-call" ||
    state === "reasoning" ||
    state === "reconnecting";

  useEffect(() => {
    if (!threadId || !promptRefId || eventSourceRef.current) {
      return;
    }

    const connect = () => {
      setState("awaiting");
      const startDate = new Date();

      eventSourceRef.current = new EventSource(
        `${getChatApiClientConfig().basePath}/chat/threads/${threadId}/stream/${promptRefId}`,
        { withCredentials: true }
      );
      const eventSource = eventSourceRef.current;

      eventSource.onmessage = (event) => {
        if (event.data === "end") {
          eventSource.close();
          handleSuccess({ threadId, startDate });
          return;
        }

        const parsedStreamData = parseStreamData(event.data);

        if ("error" in parsedStreamData) {
          eventSource.close();
          eventSourceRef.current = null;
          handleError({ error: parsedStreamData.error, threadId });
          return;
        }

        applyStateTransition(parsedStreamData, setState, startDate);

        try {
          onSuccess({ threadId, streamData: parsedStreamData });
        } catch (error) {
          eventSource.close();
          eventSourceRef.current = null;
          handleError({ error, threadId });
        }
      };

      eventSource.onerror = (_) => {
        // Silent error logging
        // appLogger.error("SSE connection error", {
        //   readyState: eventSource.readyState,
        //   type: event.type,
        //   message: "SSE connection lost or failed",
        // });

        eventSource.close();
        eventSourceRef.current = null;

        console.debug(`SSE connection error. Fetching messages again...`);
        applyStateTransition({ type: "connectionError" }, setState, startDate);

        setTimeout(() => {
          queryClient
            .refetchQueries({
              queryKey: buildGetMessagesKey({ threadId }),
            })
            .then(() => {
              handleSuccess({ threadId, startDate });
            })
            .catch((error) => {
              handleError({ error, threadId });
            });
        }, 3000);
      };
    };

    connect();
  }, [
    handleError,
    onSuccess,
    promptRefId,
    threadId,
    queryClient,
    handleSuccess,
  ]);

  useEffect(() => {
    if (!threadId || !promptRefId) {
      setState("idle");
      connectionRetriesRef.current = 0;
    }
  }, [threadId, promptRefId]);

  const resolvedState = useMemo(() => {
    const awaitingState = threadId && promptRefId ? "awaiting" : state;
    return ["idle", "success", "error"].includes(state) ? awaitingState : state;
  }, [state, threadId, promptRefId]);

  return {
    state: resolvedState,
    isReceivingResponse,
  };
};
